import React from "react";
import { Box, Typography, createStyles, withStyles } from "@material-ui/core";
import CheckIcon from "@material-ui/icons/Check";
import CheckCircleOutlineIcon from "@material-ui/icons/CheckCircleOutline";
import StarIcon from "@material-ui/icons/Star";
import MagicLinkButton from "./MagicLinkButton.web";
import { magicLinkColors, magicLinkRadius } from "./magicLinkTokens";

const RECOMMENDED_BACKGROUND = "#D8325D";
const FREQUENCY_BACKGROUND = "#FBCDDE";

interface Props {
  priceLabel: string;
  frequencyLabel?: string;
  name: string;
  description?: string;
  features?: string[];
  recommended?: boolean;
  selected?: boolean;
  savingsLabel?: string;
  onSavingsClick?: () => void;
  onSelect: () => void;
  className?: string;
  classes: Record<string, string>;
}

const styles = (theme: any) =>
  createStyles({
    card: {
      display: "flex",
      flexDirection: "column",
      gap: 12,
      padding: "16px 24px 24px",
      boxSizing: "border-box",
      borderRadius: magicLinkRadius.card,
      backgroundColor: magicLinkColors.surface,
      border: "1px solid transparent",
      cursor: "pointer",
    },
    highlighted: {
      borderColor: theme.palette.primary.main,
    },
    selected: {
      borderColor: theme.palette.primary.main,
      boxShadow: "0 4px 18px 0 rgba(0, 0, 0, 0.09)",
    },
    pillSlot: {
      minHeight: 28,
      display: "flex",
      alignItems: "center",
    },
    recommendedPill: {
      alignSelf: "flex-start",
      display: "flex",
      alignItems: "center",
      gap: 6,
      padding: "6px 8px",
      borderRadius: magicLinkRadius.card,
      backgroundColor: RECOMMENDED_BACKGROUND,
      color: magicLinkColors.surface,
      fontFamily: "Nunito Sans",
      fontSize: "12px",
      lineHeight: "16px",
    },
    pillIcon: {
      fontSize: 16,
    },
    priceRow: {
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      gap: 6,
    },
    price: {
      fontFamily: "Nunito Sans",
      fontSize: "24px",
      fontWeight: 600,
      lineHeight: "33px",
      color: magicLinkColors.textStrong,
    },
    frequency: {
      padding: "2px 6px",
      borderRadius: magicLinkRadius.card,
      backgroundColor: FREQUENCY_BACKGROUND,
      color: magicLinkColors.textStrong,
      fontFamily: "Nunito Sans",
      fontSize: "12px",
      lineHeight: "16px",
      whiteSpace: "nowrap",
    },
    line: {
      height: 1,
      backgroundColor: theme.palette.primary.main,
    },
    name: {
      fontFamily: "Nunito Sans",
      fontSize: "19px",
      fontWeight: 600,
      lineHeight: "26px",
      color: magicLinkColors.textStrong,
    },
    description: {
      fontFamily: "Nunito Sans",
      fontSize: "14px",
      fontWeight: 600,
      lineHeight: "20px",
      color: magicLinkColors.text,
    },
    features: {
      display: "flex",
      flexDirection: "column",
      gap: 12,
      flex: 1,
    },
    feature: {
      display: "flex",
      alignItems: "flex-start",
      gap: 8,
      fontFamily: "Nunito Sans",
      fontSize: "14px",
      lineHeight: "20px",
      color: magicLinkColors.text,
    },
    featureIcon: {
      fontSize: 16,
      marginTop: 2,
      color: theme.palette.primary.main,
    },
    savings: {
      fontFamily: "Nunito Sans",
      fontSize: "14px",
      fontWeight: 600,
      lineHeight: "20px",
      color: theme.palette.primary.main,
      cursor: "pointer",
      textAlign: "center",
    },
    selectButton: {
      width: "100%",
      marginTop: 12,
    },
  });

const PackageCard = ({
  priceLabel,
  frequencyLabel,
  name,
  description,
  features = [],
  recommended,
  selected,
  savingsLabel,
  onSavingsClick,
  onSelect,
  className,
  classes,
}: Props) => (
  <Box
    className={[
      classes.card,
      recommended && classes.highlighted,
      selected && classes.selected,
      className,
    ]
      .filter(Boolean)
      .join(" ")}
  >
    <Box className={classes.pillSlot} aria-hidden={!recommended}>
      {recommended && (
        <Box className={classes.recommendedPill}>
          <StarIcon className={classes.pillIcon} />
          Recommended
        </Box>
      )}
    </Box>
    <Box className={classes.priceRow}>
      <Typography className={classes.price}>{priceLabel}</Typography>
      {frequencyLabel && (
        <span className={classes.frequency}>{frequencyLabel}</span>
      )}
    </Box>
    <Box className={classes.line} />
    <Box>
      <Typography className={classes.name}>{name}</Typography>
      {description && (
        <Typography className={classes.description}>{description}</Typography>
      )}
    </Box>
    <Box className={classes.line} />
    <Box className={classes.features}>
      {features.map((feature) => (
        <Typography key={feature} component="p" className={classes.feature}>
          <CheckIcon className={classes.featureIcon} />
          {feature}
        </Typography>
      ))}
    </Box>
    {savingsLabel && (
      <Typography
        id="potentialSavings"
        className={classes.savings}
        onClick={(event) => {
          event.stopPropagation();
          if (onSavingsClick) {
            onSavingsClick();
          }
        }}
      >
        {savingsLabel}
      </Typography>
    )}
    <MagicLinkButton
      onClick={onSelect}
      className={classes.selectButton}
      startIcon={<CheckCircleOutlineIcon style={{ fontSize: 16 }} />}
    >
      {selected ? "Selected" : "Select this package"}
    </MagicLinkButton>
  </Box>
);

export default withStyles(styles)(PackageCard);
